import useModalManager from '@hooks/useModalManager';
import { Badge, StyledInput } from '@ui';
import { useTranslation } from 'react-i18next';
import SelectComponent, { Option } from '../../SelectComponent';
import ClubMemberPickerModal from '../BookingToClubMemberAssistant/ClubMemberPickerModal';
import { ServiceHoursCandidate, ServiceHoursPreviewRow, ServiceHoursRowDecision } from './types';

interface Props {
    row: ServiceHoursPreviewRow;
    decision: ServiceHoursRowDecision;
    /** Category ids the selected member is assigned to; undefined while unknown. */
    memberCategoryIds?: number[];
    /** The category the hours are booked onto, so a member outside it can be flagged. */
    categoryId?: number;
    onChange: (decision: ServiceHoursRowDecision) => void;
}

/** One preview row: the booking on the left, the member and the hours on the right. */
const ServiceHoursRow = ({ row, decision, memberCategoryIds, categoryId, onChange }: Props) => {
    const { t } = useTranslation('bookings');
    const { addModal, removeModal } = useModalManager();

    // Candidates plus the manually searched target, deduplicated, so it stays selectable
    // even though the matcher never proposed it.
    const targets: ServiceHoursCandidate[] = [
        ...row.candidates,
        ...(decision.manualTarget && !row.candidates.some((candidate) => candidate.id === decision.manualTarget?.id)
            ? [decision.manualTarget]
            : [])
    ];

    const options: Option[] = [
        { label: t('booking.creditServiceHours.actions.skip'), value: 'skip' },
        ...targets.map((candidate) => ({
            label: t('booking.creditServiceHours.actions.credit', {
                name: candidate.name,
                number: candidate.number ?? '',
                score: candidate.score
            }),
            value: candidate.id
        }))
    ];

    const selectedValue = decision.action === 'credit' ? (decision.member_id ?? undefined) : 'skip';

    // Only the reasons of the candidate actually selected are shown, so a badge can never
    // describe someone else. Manually searched targets carry none.
    const selectedCandidate =
        decision.action === 'credit' ? targets.find((candidate) => candidate.id === decision.member_id) : undefined;

    // The client-side preview of the backend's not_in_category outcome: crediting would
    // hit zero pivot rows. Only meaningful once both the member and the category are known.
    const isOutsideCategory =
        decision.action === 'credit' &&
        !!decision.member_id &&
        !!categoryId &&
        !!memberCategoryIds &&
        !memberCategoryIds.includes(categoryId);

    /** Applies the selection of the assignment dropdown. */
    const handleSelect = (value: Option['value']) => {
        if (value === 'skip') {
            onChange({ ...decision, action: 'skip', member_id: null });
            return;
        }

        onChange({ ...decision, action: 'credit', member_id: Number(value) });
    };

    /** Opens the member picker and turns the chosen member into this row's target. */
    const handleOpenPicker = () =>
        addModal({
            title: t('booking.creditServiceHours.picker.title'),
            content: (id) => (
                <ClubMemberPickerModal
                    onSelect={(candidate) => {
                        onChange({
                            ...decision,
                            action: 'credit',
                            member_id: candidate.id,
                            manualTarget: { ...candidate, reasons: [] }
                        });
                        removeModal(id);
                    }}
                />
            ),
            size: 70,
            show: true
        });

    return (
        <tr>
            <td className="tw-p-2 tw-align-top">
                <div className="tw-font-semibold">{row.booking.user_name}</div>
                <div className="tw-text-sm tw-text-accents-6">{row.booking.user_email}</div>
            </td>
            <td className="tw-p-2 tw-align-top">
                <SelectComponent options={options} value={selectedValue} onChange={handleSelect} />
                <div className="tw-mt-1 tw-flex tw-flex-wrap tw-gap-1">
                    {selectedCandidate?.reasons.map((reason) => (
                        <Badge key={reason} flat color="light" size="xs">
                            {t(`booking.creditServiceHours.reasons.${reason}`)}
                        </Badge>
                    ))}
                    {isOutsideCategory && (
                        <Badge flat color="warning" size="xs">
                            {t('booking.creditServiceHours.warnings.notInCategory')}
                        </Badge>
                    )}
                </div>
                <button
                    type="button"
                    className="tw-mt-1 tw-border-none tw-bg-transparent tw-p-0 tw-text-sm tw-text-primary tw-underline"
                    onClick={handleOpenPicker}
                >
                    {t('booking.creditServiceHours.actions.search')}
                </button>
            </td>
            <td className="tw-w-32 tw-p-2 tw-align-top">
                <StyledInput
                    bordered
                    flush
                    fullWidth
                    type="number"
                    step="0.5"
                    name={`hours_${row.booking_id}`}
                    disabled={decision.action !== 'credit'}
                    isInvalid={decision.action === 'credit' && !decision.hours}
                    value={decision.hours === '' ? '' : String(decision.hours)}
                    onChange={(event) =>
                        onChange({
                            ...decision,
                            hours: event.target.value === '' ? '' : Number(event.target.value)
                        })
                    }
                />
            </td>
        </tr>
    );
};

export default ServiceHoursRow;
